import {
	ERROR_CODES,
	type MeResponse,
	type ModerationBoardItemResponse,
	tareasQueVisorPuedeCompletar,
} from '@diario/common';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { PopUpAviso } from '../../components/ui/popUpAviso';
import { SectionLabel } from '../../components/ui/sectionLabel';
import { sesionQueryOptions, useSesion } from '../../features/auth/api/useSesion';
import {
	type AceptarSolicitudVariables,
	useAceptarSolicitud,
} from '../../features/experienceModeration/api/useAceptarSolicitud';
import { useAsistencia } from '../../features/experienceModeration/api/useAsistencia';
import { useCancelarExperiencia } from '../../features/experienceModeration/api/useCancelarExperiencia';
import { useCentroMando } from '../../features/experienceModeration/api/useCentroMando';
import { useCerrarExperiencia } from '../../features/experienceModeration/api/useCerrarExperiencia';
import { useEjecutarBaja } from '../../features/experienceModeration/api/useEjecutarBaja';
import { useRechazarSolicitud } from '../../features/experienceModeration/api/useRechazarSolicitud';
import { useSellarExperiencia } from '../../features/experienceModeration/api/useSellarExperiencia';
import { useSolicitudes } from '../../features/experienceModeration/api/useSolicitudes';
import { CancelarExperiencia } from '../../features/experienceModeration/components/cancelarExperiencia';
import { CierreExperiencia } from '../../features/experienceModeration/components/cierreExperiencia';
import { FichaSolicitud } from '../../features/experienceModeration/components/fichaSolicitud';
import { FilaExperiencia } from '../../features/experienceModeration/components/filaExperiencia';
import { usePublicarExperiencia } from '../../features/experiences/api/usePublicarExperiencia';
import { traducirError } from '../../lib/errores';
import { ApiError } from '../../lib/http';
import { useEnvioUnico } from '../../lib/useEnvioUnico';

/**
 * `/centro` (documento 06b, T-4.2): el centro de mando montado con datos reales, la ruta que
 * absorbe la única función de `_autenticado/admin.tsx` (listar pendientes y publicarlos) y mucho
 * más contexto — `/admin` se retira en esta misma tarea.
 *
 * `_autenticado.tsx` ya garantiza que hay sesión antes de llegar aquí; este `beforeLoad` añade la
 * comprobación propia de esta ruta sobre la MISMA respuesta de `GET /me` que ese padre ya cacheó
 * (`ensureQueryData` no repite la petición si la cache sigue fresca — mismo molde que
 * `_autenticado/admin.tsx` y `_autenticado/enlaces.tsx`): pasa quien sea `systemRole === 'ADMIN'`
 * o `tier === 'COLLABORATOR'` (documento 06b §6, CA-06b.1/RN-11) — **el rol se comprueba siempre
 * contra el servidor, jamás contra un parámetro de la URL**. Falla cerrado (CA-02b.7): si el
 * servidor no responde, el `catch` cierra la puerta igual que un rol/tier incorrecto — el destino
 * fuera es `/`, mismo criterio que las dos rutas que sirvieron de molde.
 */
export const Route = createFileRoute('/_autenticado/centro')({
	beforeLoad: async ({ context }) => {
		let sesion: MeResponse;
		try {
			sesion = await context.queryClient.ensureQueryData(sesionQueryOptions);
		} catch {
			throw redirect({ to: '/' });
		}
		if (sesion.systemRole !== 'ADMIN' && sesion.tier !== 'COLLABORATOR') {
			throw redirect({ to: '/' });
		}
	},
	component: PantallaCentro,
});

/** Ventana de gracia del «deshacer» tras aceptar una solicitud (decisión cerrada del usuario, ver
 * el informe de la tarea): cinco segundos en los que la fila ya se ve aceptada pero la mutación
 * real todavía no ha salido — ver el JSDoc de `useAceptarSolicitud` para el porqué de que este
 * aplazamiento viva aquí, en la ruta, y no dentro del hook. */
const VENTANA_DESHACER_MS = 5000;

/** La clave del mapa de aceptaciones en vuelo: por experiencia Y persona, porque «se puede tener
 * más de una aceptación en vuelo a la vez» (varias solicitudes aceptadas en cadena). */
const claveAceptacion = (experienceId: string, userId: string): string => `${experienceId}:${userId}`;

interface AceptacionPendiente extends AceptarSolicitudVariables {
	timeoutId: ReturnType<typeof setTimeout>;
}

/** Qué panel expandible está abierto ahora mismo (uno solo a la vez, para toda la pantalla): el
 * de solicitudes de una experiencia, el de cierre de otra, o el de cancelar una tercera (P-15,
 * añadido en esta tarea con el mismo mecanismo que las otras dos). */
type PanelActivo = { experienceId: string; tipo: 'cierre' | 'solicitudes' | 'cancelar' } | undefined;

function PantallaCentro(): React.JSX.Element {
	const { t } = useTranslation(['moderation', 'common']);
	// H-14: qué tareas rápidas puede completar de verdad quien mira, no solo cuáles trae la
	// experiencia — `sesion` puede llegar `undefined` en el primer render mientras la cache de
	// `beforeLoad` se asienta (mismo margen que `bienvenida.tsx`); hasta entonces, `esAdmin` es
	// `false`, la lectura más segura (nunca ofrece de más).
	const { sesion } = useSesion();
	const esAdmin = sesion?.systemRole === 'ADMIN';

	const experiencias = useCentroMando();
	const sellar = useSellarExperiencia();
	const publicar = usePublicarExperiencia();
	const aceptar = useAceptarSolicitud();
	const rechazar = useRechazarSolicitud();

	// B3/H-07: un cerrojo por mutación, no uno global — «Confirmar» en vuelo en la fila A no debe
	// bloquear un «Publicar» legítimo en la B (ver el JSDoc de `useEnvioUnico`). Revisar y Confirmar
	// comparten cerrojo porque comparten mutación (`sellar`): dos sellos en vuelo a la vez sobre la
	// MISMA fila tampoco tendrían sentido.
	const ejecutarSellar = useEnvioUnico();
	const ejecutarPublicar = useEnvioUnico();
	const ejecutarRechazar = useEnvioUnico();

	const [panelActivo, setPanelActivo] = useState<PanelActivo>(undefined);

	// «Última ref» al `mutate` de aceptar: la cuenta atrás y el vaciado al desmontar (más abajo)
	// necesitan llamarlo desde un `setTimeout`/una función de limpieza, fuera del ciclo de render,
	// así que no pueden capturar el `aceptar` de un render concreto.
	const aceptarMutateRef = useRef(aceptar.mutate);
	aceptarMutateRef.current = aceptar.mutate;

	const [pendientes, setPendientes] = useState<Map<string, AceptacionPendiente>>(() => new Map());
	// Espejo en una ref, actualizado tras cada render: la función de limpieza de abajo se declara
	// una única vez (dependencias vacías) y necesita ver siempre el mapa MÁS RECIENTE al
	// desmontar, no el de su primer render.
	const pendientesRef = useRef(pendientes);
	useEffect(() => {
		pendientesRef.current = pendientes;
	}, [pendientes]);

	/**
	 * Vacía las aceptaciones pendientes al desmontar esta pantalla — que es exactamente lo que
	 * pasa también al cambiar de ruta (TanStack Router desmonta el componente de la ruta anterior
	 * igual que cualquier otra navegación de React): dispara de inmediato la mutación real de
	 * cada una, en vez de dejarlas en el limbo, para no perder una aceptación por el simple hecho
	 * de haber navegado fuera de `/centro` dentro de la ventana de gracia.
	 *
	 * LÍMITE HONESTO que queda (documentado también en el informe de la tarea): esto es un efecto
	 * de React, así que solo se ejecuta si React llega a desmontar el componente. Cerrar la
	 * pestaña o la ventana de golpe DENTRO de la ventana de gracia no dispara ningún `return` de
	 * ningún efecto — esa aceptación en concreto se pierde sin más, y la solicitud se queda tal
	 * cual estaba, pendiente. Es el fallo benigno: nada se corrompe, nadie queda a medio ascender,
	 * la persona solo tiene que aceptarla otra vez.
	 */
	useEffect(() => {
		return () => {
			for (const pendiente of pendientesRef.current.values()) {
				clearTimeout(pendiente.timeoutId);
				aceptarMutateRef.current({ experienceId: pendiente.experienceId, userId: pendiente.userId });
			}
		};
	}, []);

	const alternarPanel = (experienceId: string, tipo: 'cierre' | 'solicitudes' | 'cancelar'): void => {
		setPanelActivo(anterior =>
			anterior !== undefined && anterior.experienceId === experienceId && anterior.tipo === tipo
				? undefined
				: { experienceId, tipo },
		);
	};

	/**
	 * Aceptar (CA-06b.7/decisión 12): un único clic, sin diálogo de confirmación, y la fila cambia
	 * al instante — `pendientes` se rellena YA, antes de que exista ningún temporizador que
	 * confirmarlo. La mutación real (`useAceptarSolicitud`, siempre directa: no sabe nada de esta
	 * espera) solo se dispara cuando el `setTimeout` llega al final sin que nadie pulse «deshacer».
	 *
	 * B3/H-07: `useEnvioUnico` no vale aquí porque esta función no devuelve ninguna promesa (todo
	 * el trabajo real vive en el `setTimeout` de más abajo) — el cerrojo de aquel hook se liberaría
	 * en el mismo tick, antes de que sirviera de nada. El guard síncrono es la propia `pendientesRef`
	 * (ya existe para el vaciado al desmontar): dos clics en el mismo tick sobre la MISMA solicitud
	 * comprueban la MISMA `pendientesRef.current`, así que el segundo la encuentra ya con la clave y
	 * no llega a crear un segundo temporizador. Antes de este guard, un doble clic creaba dos
	 * temporizadores para una única entrada del mapa: «Deshacer» solo cancelaba el segundo (el que
	 * quedaba en `pendientes`), y el primero, huérfano, disparaba la mutación real igualmente al
	 * agotar su ventana — «deshacer» no deshacía de verdad.
	 */
	const manejarAceptar = (experienceId: string, userId: string): void => {
		const clave = claveAceptacion(experienceId, userId);
		if (pendientesRef.current.has(clave)) {
			return;
		}
		const timeoutId = setTimeout(() => {
			setPendientes(anterior => {
				const siguiente = new Map(anterior);
				siguiente.delete(clave);
				return siguiente;
			});
			aceptarMutateRef.current({ experienceId, userId });
		}, VENTANA_DESHACER_MS);
		pendientesRef.current = new Map(pendientesRef.current).set(clave, { experienceId, timeoutId, userId });
		setPendientes(anterior => new Map(anterior).set(clave, { experienceId, timeoutId, userId }));
	};

	/** Deshacer, dentro de la ventana: cancela el temporizador antes de que llegue a disparar la
	 * mutación — el servidor nunca llega a enterarse de que hubo un «aceptar». */
	const manejarDeshacer = (experienceId: string, userId: string): void => {
		const clave = claveAceptacion(experienceId, userId);
		const pendiente = pendientesRef.current.get(clave);
		if (pendiente !== undefined) {
			clearTimeout(pendiente.timeoutId);
		}
		setPendientes(anterior => {
			const siguiente = new Map(anterior);
			siguiente.delete(clave);
			return siguiente;
		});
	};

	/** Rechazar no lleva ventana de deshacer (solo la tiene aceptar, decisión cerrada del
	 * usuario): un único clic, directo. B3/H-07: `ejecutarRechazar` (`useEnvioUnico`) cierra el
	 * hueco del doble clic síncrono. */
	const manejarRechazar = (experienceId: string, userId: string): void =>
		ejecutarRechazar(() => rechazar.mutateAsync({ experienceId, userId }));

	const renderCuerpo = (): React.JSX.Element => {
		// Cargando: esqueleto de la lista, nunca un tablero de ejemplo (§10.1).
		if (experiencias.isPending) {
			return (
				<div aria-live='polite' className='flex animate-pulse flex-col gap-4' role='status'>
					<span className='sr-only'>{t('estados.cargando')}</span>
					<div className='h-28 w-full rounded-md bg-paper-2' />
					<div className='h-28 w-full rounded-md bg-paper-2' />
					<div className='h-28 w-full rounded-md bg-paper-2' />
				</div>
			);
		}

		if (experiencias.isError) {
			// Un 403 del propio panel (rol/tier que cambió entre el guard y esta consulta, o un
			// colaborador sin experiencias asignadas) es «sin permisos» — no el mismo aviso
			// genérico y reintentable que un fallo de red o del servidor.
			const codigo = experiencias.error instanceof ApiError ? experiencias.error.code : undefined;
			if (codigo === ERROR_CODES.forbidden) {
				return <Alert variante='aviso'>{t('estados.sinPermisos')}</Alert>;
			}
			return (
				<div className='flex flex-col items-start gap-3'>
					<Alert variante='error'>{t('estados.error')}</Alert>
					<Button onClick={() => void experiencias.refetch()} type='button'>
						{t('estados.reintentar')}
					</Button>
				</div>
			);
		}

		const items = experiencias.data.items;

		if (items.length === 0) {
			// Vacío: se celebra, no se disculpa (§10.1) — `moderation:estados.vacio` ya lo dice así.
			return <Alert variante='informativo'>{t('estados.vacio')}</Alert>;
		}

		return (
			<ul className='flex flex-col gap-4'>
				{items.map(experiencia => {
					// H-14: la fila solo pinta las tareas que este visor puede completar de verdad —
					// `viewerIsOrganizer` ya viajaba en el DTO sin que nadie lo leyera.
					const experienciaVisible: ModerationBoardItemResponse = {
						...experiencia,
						quickTasks: tareasQueVisorPuedeCompletar(experiencia.quickTasks, {
							esAdmin,
							esOrganizador: experiencia.viewerIsOrganizer,
						}),
					};
					// El error de "revisar"/"confirmar" o "publicar" más reciente, solo si esta fue la
					// fila que lo disparó (`mutation.variables` conserva el argumento de la última
					// llamada): así el aviso queda pegado a la fila afectada, no a la cabecera de toda
					// la lista (H-14).
					const errorSellarDeEstaFila =
						sellar.isError && sellar.variables?.id === experiencia.id ? sellar.error : undefined;
					const errorPublicarDeEstaFila =
						publicar.isError && publicar.variables === experiencia.id ? publicar.error : undefined;
					const errorDeEstaFila = errorSellarDeEstaFila === undefined ? errorPublicarDeEstaFila : errorSellarDeEstaFila;
					// T-1.2: los dos paneles expandibles de esta fila van AQUÍ, dentro de su propio
					// `Fragment` — antes se pintaban una única vez, sueltos tras el `<ul>` entero, así que
					// con varias filas por delante el panel podía quedar cientos de píxeles por debajo del
					// botón que lo abría (`PanelSolicitudes`/`PanelCierre` ya documentaban "bajo su fila",
					// la implementación no lo cumplía). `panelActivo` es uno solo para toda la pantalla, así
					// que cada fila comprueba si es LA SUYA antes de montar el panel que le toca.
					const panelSolicitudesDeEstaFila =
						panelActivo?.tipo === 'solicitudes' && panelActivo.experienceId === experiencia.id;
					const panelCierreDeEstaFila = panelActivo?.tipo === 'cierre' && panelActivo.experienceId === experiencia.id;
					const panelCancelarDeEstaFila =
						panelActivo?.tipo === 'cancelar' && panelActivo.experienceId === experiencia.id;
					// P-15: cancelar solo tiene sentido para quien organiza o administra (mismo criterio de
					// autorizacion que el servicio, `puedeGestionarExperiencia`) y solo mientras el plan siga
					// vivo: `archivada` agrupa `FINISHED`/`CANCELLED`/`TRASH` (ver `common/rules/moderation.ts`),
					// y ninguno de los tres es un plan con sentido de volver a cancelar.
					//
					// `sinOtroBloqueEnEstaFila` (además) es lo que evita romper `tests/centroRuta.test.tsx`
					// (T-1.2/H-14, ajeno a esta tarea): esos tests comprueban con `nextElementSibling` que la
					// fila SOLO tiene como hermano inmediato su error o su panel abierto, nada más — así que
					// este disparador únicamente se pinta cuando esta fila no tiene ya un error ni un panel
					// propio abierto (siempre en última posición, nunca entre la fila y esos dos). Cuando SÍ
					// hay panel de cancelar abierto (`panelCancelarDeEstaFila`), el disparador se sustituye por
					// el propio botón «Cancelar experiencia» de `CancelarExperiencia`, dentro de ese panel — no
					// se pierde la acción, solo deja de duplicarse.
					const sinOtroBloqueEnEstaFila =
						errorDeEstaFila === undefined
						&& !panelSolicitudesDeEstaFila
						&& !panelCierreDeEstaFila
						&& !panelCancelarDeEstaFila;
					const puedeCancelar =
						(esAdmin || experiencia.viewerIsOrganizer) && experiencia.phase !== 'archivada' && sinOtroBloqueEnEstaFila;
					return (
						<Fragment key={experiencia.id}>
							<FilaExperiencia
								experiencia={experienciaVisible}
								onCerrar={() => alternarPanel(experiencia.id, 'cierre')}
								onConfirmar={() =>
									ejecutarSellar(() => sellar.mutateAsync({ datos: { confirmed: true }, id: experiencia.id }))
								}
								onPublicar={() =>
									// `usePublicarExperiencia` (congelado, ajeno a esta tarea) invalida el listado
									// básico de `useExperiences` y su ficha, pero no `centroMandoQueryKey()` — el
									// callback de éxito de esta llamada concreta refresca el panel sin tocar ese hook.
									ejecutarPublicar(() =>
										publicar.mutateAsync(experiencia.id, { onSuccess: () => void experiencias.refetch() }),
									)
								}
								onRevisar={() =>
									ejecutarSellar(() => sellar.mutateAsync({ datos: { reviewed: true }, id: experiencia.id }))
								}
								onVerSolicitudes={() => alternarPanel(experiencia.id, 'solicitudes')}
							/>
							{errorDeEstaFila === undefined ? null : (
								<li>
									<Alert variante='error'>{t(`common:errores.${traducirError(errorDeEstaFila)}`)}</Alert>
								</li>
							)}
							{panelSolicitudesDeEstaFila ? (
								<li>
									<PanelSolicitudes
										error={aceptar.error ?? rechazar.error}
										esAdmin={esAdmin}
										experienceId={experiencia.id}
										mostrarError={aceptar.isError || rechazar.isError}
										onAceptar={userId => manejarAceptar(experiencia.id, userId)}
										onDeshacer={userId => manejarDeshacer(experiencia.id, userId)}
										onRechazar={userId => manejarRechazar(experiencia.id, userId)}
										pendientes={pendientes}
										titulo={experiencia.title}
									/>
								</li>
							) : null}
							{panelCierreDeEstaFila ? (
								<li>
									<PanelCierre experiencia={experiencia} />
								</li>
							) : null}
							{panelCancelarDeEstaFila ? (
								<li>
									<PanelCancelar experiencia={experiencia} />
								</li>
							) : null}
							{/* P-15: el disparador de «Cancelar experiencia» vive AQUÍ, fuera de `FilaExperiencia`
							(congelada para esta tarea, sin ningún hueco `on…` para esta acción) — mismo
							mecanismo de `panelActivo` que ya usan «Cerrar»/«Atender solicitudes». Va SIEMPRE en
							última posición y solo si `sinOtroBloqueEnEstaFila` (ver el JSDoc de `puedeCancelar`,
							más arriba): así nunca se cuela entre la fila y su error/panel abierto, que es
							justo lo que comprueban con `nextElementSibling` los tests T-1.2/H-14 de
							`tests/centroRuta.test.tsx` (ajenos a esta tarea). */}
							{puedeCancelar ? (
								<li>
									<Button
										onClick={() => alternarPanel(experiencia.id, 'cancelar')}
										size='sm'
										type='button'
										variant='ghost'
									>
										{t('cancelacion.accion')}
									</Button>
								</li>
							) : null}
						</Fragment>
					);
				})}
			</ul>
		);
	};

	return (
		<section className='flex flex-col gap-6'>
			<div className='space-y-2'>
				<h1 className='font-display text-2xl text-ink'>{t('panel.titulo')}</h1>
				<p className='text-base text-gray-ink'>{t('panel.descripcion')}</p>
			</div>
			{renderCuerpo()}
		</section>
	);
}

interface PanelSolicitudesProps {
	/** El error más reciente de aceptar o de rechazar (lo que haya ocurrido último), para pintar
	 * un único aviso — ninguna de las dos mutaciones lo atrapa ni lo reinterpreta por su cuenta. */
	error?: unknown;
	/** Cierto solo para `systemRole === 'ADMIN'` (P-18, segunda vía de la baja): un colaborador ve
	 * esta misma ficha de solicitudes sin el control de «Dar de baja» — solo administración puede
	 * ejecutarla (`@Roles(ADMIN)` en `POST /users/:id/deletion`, comprobado también aquí para no
	 * ofrecer un botón que el servidor rechazaría). */
	esAdmin: boolean;
	experienceId: string;
	mostrarError: boolean;
	onAceptar: (userId: string) => void;
	onDeshacer: (userId: string) => void;
	onRechazar: (userId: string) => void;
	/** Las aceptaciones en vuelo de TODA la pantalla (vive en `PantallaCentro`, ver su JSDoc):
	 * este panel solo lee si la clave de `(experienceId, userId)` está dentro. */
	pendientes: Map<string, AceptacionPendiente>;
	/** Título de la experiencia (T-1.2): quien monta este panel puede tener varias filas de títulos
	 * casi idénticos delante, así que el rótulo del panel tiene que decir de cuál está hablando —
	 * se interpola en `panelSolicitudes.tituloCon`. */
	titulo: string;
}

/**
 * Las solicitudes de UNA experiencia, desplegadas bajo su fila (documento 06b §10.4). A
 * diferencia de `pendientes` (que vive en `PantallaCentro` para sobrevivir a que se cierre este
 * panel), este componente posee su propia consulta — se monta y desmonta con el propio
 * despliegue de «atender solicitudes», así que perder su caché al cerrarlo no tiene coste: se
 * vuelve a pedir si se abre otra vez.
 */
const PanelSolicitudes = ({
	error,
	esAdmin,
	experienceId,
	mostrarError,
	onAceptar,
	onDeshacer,
	onRechazar,
	pendientes,
	titulo,
}: PanelSolicitudesProps): React.JSX.Element => {
	const { t } = useTranslation(['moderation', 'common']);
	const idTitulo = useId();
	const solicitudes = useSolicitudes(experienceId);

	return (
		<Card aria-labelledby={idTitulo} as='section' className='flex flex-col gap-3' variant='sunken'>
			<SectionLabel id={idTitulo} number={1}>
				{t('panelSolicitudes.tituloCon', { titulo })}
			</SectionLabel>

			{mostrarError ? <Alert variante='error'>{t(`common:errores.${traducirError(error)}`)}</Alert> : null}

			{solicitudes.isPending ? (
				<p aria-live='polite' role='status'>
					{t('estados.cargando')}
				</p>
			) : null}

			{solicitudes.isError ? (
				<div className='flex flex-col items-start gap-2'>
					<Alert variante='error'>{t('estados.error')}</Alert>
					<Button onClick={() => void solicitudes.refetch()} type='button'>
						{t('estados.reintentar')}
					</Button>
				</div>
			) : null}

			{solicitudes.data !== undefined && solicitudes.data.items.length === 0 ? (
				<Alert variante='informativo'>{t('espera.sinSolicitudes')}</Alert>
			) : null}

			{solicitudes.data !== undefined && solicitudes.data.items.length > 0 ? (
				// CA-06b.5: en el orden que devuelve el servidor — sin `.sort()` de ningún tipo aquí.
				<ul className='flex flex-col gap-3'>
					{solicitudes.data.items.map(solicitud => (
						<li className='flex flex-col gap-2' key={solicitud.id}>
							<FichaSolicitud
								onAceptar={() => onAceptar(solicitud.userId)}
								onDeshacer={() => onDeshacer(solicitud.userId)}
								onRechazar={() => onRechazar(solicitud.userId)}
								pendienteDeConfirmar={pendientes.has(claveAceptacion(experienceId, solicitud.userId))}
								solicitud={solicitud}
								tituloExperiencia={titulo}
							/>
							{/* P-18, segunda vía: `FichaSolicitud` está congelada para esta tarea (sin ningún
							hueco `on…` para esta acción), así que «Dar de baja» vive aquí al lado, fuera de
							esa ficha — visible solo para administración. */}
							{esAdmin ? (
								<div className='flex justify-end'>
									<EjecutarBaja userId={solicitud.userId} />
								</div>
							) : null}
						</li>
					))}
				</ul>
			) : null}
		</Card>
	);
};

interface PanelCierreProps {
	/** La fila del panel (`useCentroMando`, ya cargada) de la experiencia que se está cerrando:
	 * de aquí salen `status` (¿ya está `FINISHED`?) y las fechas (¿todavía no ha pasado?), que
	 * `useAsistencia` no trae — es la ficha de asistencia, no la de la experiencia. */
	experiencia: ModerationBoardItemResponse;
}

/** Cierra UNA experiencia y pasa lista (documento 06b §10.2), con los datos de `useAsistencia`. */
const PanelCierre = ({ experiencia }: PanelCierreProps): React.JSX.Element => {
	const { t } = useTranslation(['moderation', 'common']);
	const idTitulo = useId();
	const asistencia = useAsistencia(experiencia.id);
	const cerrar = useCerrarExperiencia();
	// T-1.2: mismo motivo que `panelSolicitudes.tituloCon` — con varias filas de títulos casi
	// idénticos, el rótulo tiene que decir de cuál está hablando, en las tres ramas de abajo.
	const tituloPanel = t('panelCierre.tituloCon', { titulo: experiencia.title });

	if (asistencia.isPending) {
		return (
			<div className='flex flex-col gap-3'>
				<SectionLabel id={idTitulo}>{tituloPanel}</SectionLabel>
				<p aria-live='polite' role='status'>
					{t('estados.cargando')}
				</p>
			</div>
		);
	}

	if (asistencia.isError) {
		return (
			<div className='flex flex-col gap-3'>
				<SectionLabel id={idTitulo}>{tituloPanel}</SectionLabel>
				<div className='flex flex-col items-start gap-2'>
					<Alert variante='error'>{t('estados.error')}</Alert>
					<Button onClick={() => void asistencia.refetch()} type='button'>
						{t('estados.reintentar')}
					</Button>
				</div>
			</div>
		);
	}

	// «Ya pasó» (mismo criterio que `unirse.$id.tsx`): sin fecha de fin, se mira la de inicio.
	const fechaReferencia = experiencia.endDate ?? experiencia.startDate;
	const fechaFutura = fechaReferencia != null && new Date(fechaReferencia).getTime() > Date.now();

	return (
		<Card aria-labelledby={idTitulo} as='section' className='flex flex-col gap-3' variant='sunken'>
			<SectionLabel id={idTitulo}>{tituloPanel}</SectionLabel>
			{cerrar.isError ? <Alert variante='error'>{t(`common:errores.${traducirError(cerrar.error)}`)}</Alert> : null}
			<CierreExperiencia
				asistencia={asistencia.data}
				enviando={cerrar.isPending}
				fechaFutura={fechaFutura}
				onCerrar={absentUserIds => cerrar.mutate({ datos: { absentUserIds, acknowledged: true }, id: experiencia.id })}
				yaCerrada={experiencia.status === 'FINISHED'}
			/>
		</Card>
	);
};

interface PanelCancelarProps {
	/** La fila del panel (`useCentroMando`, ya cargada) de la experiencia que se está cancelando:
	 * de aquí salen `title` (para la plantilla del mensaje de WhatsApp) e `id`. */
	experiencia: ModerationBoardItemResponse;
}

/**
 * Cancela UN plan (P-15), con motivo obligatorio y el mensaje de WhatsApp ya redactado tras
 * hacerlo. Mismo molde que `PanelCierre`: este envoltorio pide los datos y posee la mutación
 * (`useCancelarExperiencia`, propia de esta tarea), `CancelarExperiencia` es presentacional puro.
 *
 * `cancelada`/`motivoCancelado` se leen de `cancelar.variables`, no de un estado propio de este
 * componente: es el mismo criterio que `errorDeEstaFila` en `PantallaCentro` — solo si ESTA fila
 * disparó la mutación (`cancelar.variables?.id === experiencia.id`), nunca la última cancelación
 * de cualquier otra fila de la pantalla (`useCancelarExperiencia` es un único hook por fila, así
 * que en la práctica esto ya lo garantiza, pero se deja explícito por si el día de mañana el hook
 * se comparte más arriba).
 */
const PanelCancelar = ({ experiencia }: PanelCancelarProps): React.JSX.Element => {
	const { t } = useTranslation(['moderation', 'common']);
	const idTitulo = useId();
	const cancelar = useCancelarExperiencia();
	const cancelada = cancelar.isSuccess && cancelar.variables?.id === experiencia.id;

	return (
		<Card aria-labelledby={idTitulo} as='section' className='flex flex-col gap-3' variant='sunken'>
			<SectionLabel id={idTitulo}>{t('cancelacion.accion')}</SectionLabel>
			{cancelar.isError ? <Alert variante='error'>{t(`common:errores.${traducirError(cancelar.error)}`)}</Alert> : null}
			<CancelarExperiencia
				cancelada={cancelada}
				enviando={cancelar.isPending}
				motivoCancelado={cancelar.variables?.datos.reason}
				onCancelar={reason => cancelar.mutate({ datos: { reason }, id: experiencia.id })}
				title={experiencia.title}
			/>
		</Card>
	);
};

interface EjecutarBajaProps {
	/** El `id` de la persona a dar de baja — `moderationRequestResponseSchema.userId`, el mismo
	 * identificador que ya usa `FichaSolicitud` para aceptar/rechazar. */
	userId: string;
}

/**
 * Ejecuta la baja de una persona (`moderation:baja.*`, P-18, segunda vía — solo administración),
 * inline junto a su ficha de solicitud: `fichaSolicitud.tsx` está congelada para esta tarea, así
 * que este control no puede vivir dentro de esa ficha, y `moderation:baja` no trae una clave de
 * panel/`tituloCon` propia (a diferencia de `cierre`/`panelSolicitudes`) que justifique sacarlo a
 * su propio componente — vive aquí, en `centro.tsx`, el único fichero de esta tarea desde el que
 * `PanelSolicitudes` es accesible.
 *
 * Es una acción destructiva y sin deshacer, así que exige confirmación explícita — `PopUpAviso`
 * (congelado), con `image={null}` (mismo criterio que `CierreExperiencia`/`CancelarExperiencia`:
 * esto no es un momento de marca). El botón principal necesita poder deshabilitarse mientras la
 * mutación está en curso, así que viaja por `children`, no por `actionLabel` (mismo motivo
 * documentado en `CierreExperiencia`/`CancelarExperiencia`: ese contrato no expone un `disabled`
 * en su acción principal).
 *
 * **LÍMITE CONOCIDO, respetado a propósito (respuestas del cliente 2026-09-17, P-18; ver también
 * el JSDoc de `UsersService.ejecutarBaja` en el backend)**: los planes futuros que esta persona
 * organiza NO se cancelan con esta acción — cancelarlos es la acción separada «Cancelar
 * experiencia», plan a plan. El aviso de aquí NO dice lo contrario.
 *
 * **Clave i18n que falta, reportada en vez de inventada (informe de la tarea)**: el encargo pide
 * que el aviso explique «qué pasa con lo que deja atrás» (sus plazas se liberan, sus enlaces de
 * organizador se revocan, su foto se retira), pero `moderation:baja.*` solo trae
 * `accion`/`titulo`/`cancelar`/`confirmar`/`exito` — ninguna clave de cuerpo para ese texto. Existe
 * `auth:baja.queDejasAtras.*`, pero está escrita en primera persona («Tus enlaces...», para la
 * autobaja desde el propio perfil) y su línea `.planes` afirma que los planes futuros se cancelan
 * o se traspasan — falso para esta vía (ver el límite de arriba): reutilizarla sería mentir. Este
 * diálogo se queda solo con `titulo`/`confirmar`/`cancelar` hasta que exista una clave de
 * `moderation:baja` en tercera persona para ese cuerpo, sin las líneas de planes/valoraciones que
 * no aplican aquí.
 */
const EjecutarBaja = ({ userId }: EjecutarBajaProps): React.JSX.Element => {
	const { t } = useTranslation(['moderation', 'common']);
	const [confirmando, setConfirmando] = useState(false);
	const baja = useEjecutarBaja();

	if (baja.isSuccess) {
		return <Alert variante='exito'>{t('baja.exito')}</Alert>;
	}

	const cancelarConfirmacion = (): void => setConfirmando(false);

	return (
		<>
			<Button aria-haspopup='dialog' onClick={() => setConfirmando(true)} size='sm' type='button' variant='ghost'>
				{t('baja.accion')}
			</Button>
			<PopUpAviso image={null} onClose={cancelarConfirmacion} open={confirmando} title={t('baja.titulo')}>
				{baja.isError ? <Alert variante='error'>{t(`common:errores.${traducirError(baja.error)}`)}</Alert> : null}
				<div className='mt-2 flex flex-wrap justify-center gap-2'>
					<Button
						className='min-h-11'
						disabled={baja.isPending}
						onClick={cancelarConfirmacion}
						type='button'
						variant='secondary'
					>
						{t('baja.cancelar')}
					</Button>
					<Button
						className='min-h-11'
						disabled={baja.isPending}
						onClick={() => baja.mutate(userId)}
						type='button'
						variant='dark'
					>
						{t('baja.confirmar')}
					</Button>
				</div>
			</PopUpAviso>
		</>
	);
};
